import type { Numberish, TBaseCommonCode, TBaseResponse, TPhaseCode, TPhaseCodeCreate, TSelectorItem } from "@/models/common.model"
import type { TSettings, TSettingsRaw } from "@/models/discipline.model"
import type { Phase, PhaseBody, TPhaseApiResponse, TPhaseAutomaticBody } from "@/models/phases.model"
import type { HttpClient } from "@/services/http-common"
import { parse } from "date-fns"
import { tryCatch } from "@/lib/try-catch"
import { PhasesMapper } from "@/mappers/competition/phases.mapper"
import { SelectorMapper } from "@/mappers/selectors.mapper"
import ApiServiceFactory from "@/services/api-service-factory"
import { ApiError } from "@/services/http-common"

export type TMassiveUpdateDates = {
  rscCode: string[]
  dateInfo: {
    startDate: string | undefined
    endDate: string | undefined
  }
}

export type TMassiveUpdateVenues = {
  rscCode: string[]
  venueCodes: string[]
}

/**
 * Service for Badminton API
 */
export class PhasesService {
  private httpClient: HttpClient

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  /**
   * Fetch foul categories from the API
   */
  async fetchAllPhases(): Promise<Phase[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TPhaseApiResponse[]>>("/phase/get-all")
      return PhasesMapper.toDto([...response.value])
    }
    catch (error) {
      console.error("Error fetching foul categories:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No phases found, returning empty array")
        return []
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  async getPhasesByEventCode(eventCode: string): Promise<Phase[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TPhaseApiResponse[]>>(`/phase/get-by-sport-event/${eventCode}`)
      return PhasesMapper.toDto(response.value)
    }
    catch (error) {
      console.error("Error fetching phases:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No phases found, returning empty array")
        return []
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  async getPhasesByCopmpetitionCode(competitionCode: string): Promise<Phase[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TPhaseApiResponse[]>>(`/phase/get-by-competition-code/${competitionCode}`)
      return PhasesMapper.toDto(response.value)
    }
    catch (error) {
      console.error("Error fetching phases:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No phases found, returning empty array")
        return []
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  async getSettingsByCompetitionCode(competitionCode: string): Promise<TSettings> {
    const response = await tryCatch(this.httpClient.get<TBaseResponse<TSettingsRaw>>(`/competitions/competition-details/${competitionCode}`))

    if (response.error) {
      console.error("Error fetching settings by competition code:", response.error)

      // Si el error es 404, devolvemos un objeto vacío
      if (response.error.statusCode === 404) {
        console.info("No settings found for competition code, returning empty object")
        return {} as TSettings
      }

      // Para cualquier otro tipo de error, lo lanzamos
      throw new Error(response.error.message || "Unknown error fetching settings by competition code")
    }
    console.log("Fetched settings by competition code:", response.data)
    return {
      ...response.data,
      dateInfo: {
        startDate: response.data.dateInfo?.startDate ? parse(response.data.dateInfo.startDate, "yyyy-MM-dd'T'HH:mm:ss", new Date()) : undefined,
        endDate: response.data.dateInfo?.endDate ? parse(response.data.dateInfo.endDate, "yyyy-MM-dd'T'HH:mm:ss", new Date()) : undefined,
      },
    }
  }

  async getPhaseByCode(phaseCode: string): Promise<Phase> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TPhaseApiResponse>>(`/phase/get-by-code/${phaseCode}`)
      const phases = PhasesMapper.toDto([response.value])
      return phases[0] || PhasesMapper.toDtoSingle(null)
    }
    catch (error) {
      console.error("Error fetching phase by code:", error)
      throw error
    }
  }

  async savePhase(data: PhaseBody) {
    try {
      return await this.httpClient.post<unknown>("/phase/create", data)
    }
    catch (error) {
      console.log("Error saving phase", error)
      throw error
    }
  }

  async savePhaseAutomatically(data: TPhaseAutomaticBody) {
    try {
      return await this.httpClient.post<unknown>("/phase/create-many", data)
    }
    catch (error) {
      console.log("Error saving phase", error)
      throw error
    }
  }

  async updatePhase(data: PhaseBody) {
    try {
      return await this.httpClient.put<unknown>("/phase/update", data)
    }
    catch (error) {
      console.log("Error updating phase", error)
      throw error
    }
  }

  async deletePhase(phaseId: string) {
    try {
      return await this.httpClient.delete<unknown>(`/phase/${phaseId}`)
    }
    catch (error) {
      console.log("Error deleting phase", error)
      throw error
    }
  }

  async createPhaseCode(data: TPhaseCodeCreate) {
    try {
      return await this.httpClient.post<unknown>("/phases-code/create", data)
    }
    catch (error) {
      console.log("Error creating phase type", error)
      throw error
    }
  }

  async updatePhaseCode(id: string, data: TPhaseCodeCreate) {
    try {
      return await this.httpClient.put<unknown>(`/phases-code/update/${id}`, data)
    }
    catch (error) {
      console.log("Error updating phase code", error)
      throw error
    }
  }

  async deletePhaseCode(id: string) {
    try {
      return await this.httpClient.delete<unknown>(`/phases-code/delete/${id}`)
    }
    catch (error) {
      console.log("Error deleting phase code", error)
      throw error
    }
  }

  async fetchPhaseTypes(): Promise<TSelectorItem[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TBaseCommonCode[]>>("/phases-code/get-all")

      const { value } = response

      return SelectorMapper.toDto(value)
    }
    catch (error) {
      throw new Error(`Error fetching phase types: 
        ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }

  async fetchPhaseTypesByCompetition(competitionId: string): Promise<TSelectorItem[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TBaseCommonCode[]>>(`/phases-code/get-by-competition/${competitionId}`)

      const { value } = response

      return SelectorMapper.toDto(value)
    }
    catch (error) {
      throw new Error(`Error fetching phase types by competition: 
        ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }

  async getPhasesByDiscipline(disciplineCode: string): Promise<Phase[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TPhaseApiResponse[]>>(`/phases-code/get-by-discipline/${disciplineCode}`)
      return PhasesMapper.toDto(response.value)
    }
    catch (error) {
      console.error("Error fetching phases:", error)
      throw error
    }
  }

  async getPhaseCodesByDiscipline(disciplineCode: string): Promise<TSelectorItem[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TPhaseCode[]>>(`/phases-code/get-by-discipline/${disciplineCode}`)
      return SelectorMapper.toDto(response.value)
    }
    catch (error) {
      console.error("Error fetching phase codes by discipline:", error)
      throw error
    }
  }

  async updateVenues(phaseCode: string, venues: Numberish[]): Promise<void> {
    try {
      await this.httpClient.put<void>(`/phase/update-venues/${phaseCode}`, { venues })
    }
    catch (error) {
      console.error("Error updating phase venues:", error)
      throw error
    }
  }

  async updateDates(phaseCode: string, dates: { startDate: string | undefined, endDate: string | undefined }): Promise<void> {
    try {
      await this.httpClient.put<void>(`/phase/update-dates/${phaseCode}`, dates)
    }
    catch (error) {
      console.error("Error updating phase dates:", error)
      throw error
    }
  }

  async updateMasiveDates(settings: TMassiveUpdateDates): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`/phase/massive-update-dates`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las fechas masivamente:", (error as Error).message)
      throw error
    }
  }

  async updateMasiveVenues(settings: TMassiveUpdateVenues): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`/phase/massive-update-venues`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las venues masivamente:", (error as Error).message)
      throw error
    }
  }
}

// Create and export a singleton instance of the BadmintonService
export const phasesService = ApiServiceFactory.createService(PhasesService, "bdm")

export default PhasesService
